import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Layers, Plus, Search, Edit2, Trash2, X, AlertCircle, Loader2, CheckCircle,
} from 'lucide-react';
import { getAuthHeaders } from '../utils/authHeaders';

const API_BASE = process.env.REACT_APP_API_URL || 'https://predworks.alliantprojects.com/api/v1';

type StageLevel = 'ABSTRACT' | 'SUB' | 'DETAILED';

interface WorkStage {
  id: number;
  work_type_id: number | null;
  stage_level: StageLevel;
  name: string;
  code: string;
  sequence_order: number;
  is_active: boolean;
}

interface WorkType { work_type_id: number; work_type_name: string; }

type StageForm = Partial<WorkStage>;

const EMPTY_FORM: StageForm = {
  stage_level: 'SUB', work_type_id: null, name: '', code: '', sequence_order: 0, is_active: true,
};

const LEVELS: StageLevel[] = ['ABSTRACT', 'SUB', 'DETAILED'];

const LEVEL_BADGE: Record<StageLevel, string> = {
  ABSTRACT: 'bg-purple-100 text-purple-700',
  SUB: 'bg-blue-100 text-blue-700',
  DETAILED: 'bg-emerald-100 text-emerald-700',
};

async function api<T>(path: string, options: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders(), ...(options.headers || {}) },
  });
  if (!res.ok) {
    const e = await res.json().catch(() => ({}));
    throw new Error(e.message ? (Array.isArray(e.message) ? e.message.join(', ') : e.message) : `Error ${res.status}`);
  }
  if (res.status === 204) return undefined as unknown as T;
  return res.json();
}

const unwrap = (j: any): any[] => (Array.isArray(j) ? j : (j?.data ?? j?.items ?? []));

// The list API caps page size at 100, so page through until all rows are fetched.
async function fetchAllPages<T = any>(path: string): Promise<T[]> {
  const all: T[] = [];
  let page = 1;
  for (;;) {
    const sep = path.includes('?') ? '&' : '?';
    const resp = await api<any>(`${path}${sep}page=${page}&limit=100`);
    const rows = unwrap(resp);
    all.push(...(rows as T[]));
    if (!resp?.meta?.hasNext || rows.length === 0 || page > 100) break;
    page++;
  }
  return all;
}

const StagesManagement: React.FC = () => {
  const [stages, setStages] = useState<WorkStage[]>([]);
  const [workTypes, setWorkTypes] = useState<WorkType[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [levelFilter, setLevelFilter] = useState<'' | StageLevel>('');
  const [typeFilter, setTypeFilter] = useState<string>(''); // '', 'generic', or work_type_id

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<StageForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [deleteTarget, setDeleteTarget] = useState<WorkStage | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [stageRows, typesResp] = await Promise.all([
        fetchAllPages<WorkStage>('/work-stages?sortBy=sequence_order&sortOrder=ASC'),
        api<any>('/master-data/work-types'),
      ]);
      setStages(stageRows);
      setWorkTypes(unwrap(typesResp) as WorkType[]);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load stages');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!notice) return;
    const t = setTimeout(() => setNotice(null), 3500);
    return () => clearTimeout(t);
  }, [notice]);

  const typeName = useCallback((id: number | null) => {
    if (id == null) return 'Generic (all types)';
    return workTypes.find((t) => t.work_type_id === id)?.work_type_name || `Type #${id}`;
  }, [workTypes]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return stages.filter((s) => {
      if (levelFilter && s.stage_level !== levelFilter) return false;
      if (typeFilter === 'generic' && s.work_type_id != null) return false;
      if (typeFilter && typeFilter !== 'generic' && s.work_type_id !== Number(typeFilter)) return false;
      if (q && ![s.name, s.code, s.stage_level].some((v) => String(v).toLowerCase().includes(q))) return false;
      return true;
    });
  }, [stages, search, levelFilter, typeFilter]);

  const openCreate = () => { setEditingId(null); setForm(EMPTY_FORM); setFormError(null); setShowModal(true); };
  const openEdit = (s: WorkStage) => { setEditingId(s.id); setForm({ ...s }); setFormError(null); setShowModal(true); };
  const setField = (k: keyof StageForm, v: any) => setForm((p) => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.name?.trim()) { setFormError('Stage name is required'); return; }
    if (!form.code?.trim()) { setFormError('Stage code is required'); return; }
    if (!form.stage_level) { setFormError('Stage level is required'); return; }
    try {
      setSaving(true);
      setFormError(null);
      const payload = {
        stage_level: form.stage_level,
        work_type_id: form.work_type_id === undefined || (form.work_type_id as any) === '' ? null : form.work_type_id,
        name: form.name.trim(),
        code: form.code.trim(),
        sequence_order: Number(form.sequence_order) || 0,
        is_active: form.is_active !== false,
      };
      if (editingId) {
        await api(`/work-stages/${editingId}`, { method: 'PUT', body: JSON.stringify(payload) });
        setNotice('Stage updated successfully');
      } else {
        await api('/work-stages', { method: 'POST', body: JSON.stringify(payload) });
        setNotice('Stage created successfully');
      }
      setShowModal(false);
      await load();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Failed to save stage');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await api(`/work-stages/${deleteTarget.id}`, { method: 'DELETE' });
      setNotice('Stage deleted successfully');
      setDeleteTarget(null);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to delete stage');
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="flex items-center">
          <Layers className="w-7 h-7 text-emerald-600 mr-3" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Stages of Work</h1>
            <p className="text-sm text-gray-500">Abstract, sub and detailed stages used across works &amp; progress</p>
          </div>
        </div>
        <button onClick={openCreate}
          className="inline-flex items-center px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors">
          <Plus className="w-4 h-4 mr-2" /> Add Stage
        </button>
      </div>

      {notice && (
        <div className="mb-4 flex items-center bg-green-50 border border-green-200 text-green-800 rounded-lg px-4 py-2 text-sm">
          <CheckCircle className="w-4 h-4 mr-2" /> {notice}
        </div>
      )}
      {error && (
        <div className="mb-4 flex items-center bg-red-50 border border-red-200 text-red-800 rounded-lg px-4 py-2 text-sm">
          <AlertCircle className="w-4 h-4 mr-2" /> {error}
        </div>
      )}

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <div className="relative">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name / code…"
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm" />
        </div>
        <select value={levelFilter} onChange={(e) => setLevelFilter(e.target.value as any)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 text-sm">
          <option value="">All Levels</option>
          {LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 text-sm">
          <option value="">All Work Types</option>
          <option value="generic">Generic (all types)</option>
          {workTypes.map((t) => <option key={t.work_type_id} value={t.work_type_id}>{t.work_type_name}</option>)}
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-gray-500"><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Loading stages…</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-gray-500">
            <Layers className="w-10 h-10 mx-auto mb-2 text-gray-300" />
            {stages.length ? 'No stages match your filters.' : 'No stages yet. Click “Add Stage”.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {['Seq', 'Level', 'Name', 'Code', 'Work Type', 'Active', ''].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((s) => (
                  <tr key={s.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-500">{s.sequence_order}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${LEVEL_BADGE[s.stage_level]}`}>{s.stage_level}</span>
                    </td>
                    <td className="px-4 py-3 font-medium text-gray-900">{s.name}</td>
                    <td className="px-4 py-3 text-gray-600 font-mono text-xs">{s.code}</td>
                    <td className="px-4 py-3 text-gray-700">
                      {s.work_type_id == null
                        ? <span className="text-gray-400 italic">Generic</span>
                        : typeName(s.work_type_id)}
                    </td>
                    <td className="px-4 py-3">
                      {s.is_active
                        ? <span className="text-xs text-green-700 bg-green-100 px-1.5 py-0.5 rounded">Active</span>
                        : <span className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">Inactive</span>}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button onClick={() => openEdit(s)} title="Edit" className="p-1.5 text-gray-500 hover:text-emerald-600 hover:bg-emerald-50 rounded"><Edit2 className="w-4 h-4" /></button>
                      <button onClick={() => setDeleteTarget(s)} title="Delete" className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded ml-1"><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <p className="text-xs text-gray-400 mt-2">{filtered.length} stage(s){(search || levelFilter || typeFilter) ? ` (filtered from ${stages.length})` : ''}</p>

      {/* Create / Edit modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">{editingId ? 'Edit Stage' : 'Add Stage'}</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              {formError && (
                <div className="flex items-center bg-red-50 border border-red-200 text-red-800 rounded-lg px-3 py-2 text-sm">
                  <AlertCircle className="w-4 h-4 mr-2" /> {formError}
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Stage Level *">
                  <select className={inp} value={form.stage_level || ''} onChange={(e) => setField('stage_level', e.target.value)}>
                    {LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
                  </select>
                </Field>
                <Field label="Work Type">
                  <select className={inp}
                    value={form.work_type_id == null ? '' : String(form.work_type_id)}
                    onChange={(e) => setField('work_type_id', e.target.value === '' ? null : Number(e.target.value))}>
                    <option value="">Generic (all types)</option>
                    {workTypes.map((t) => <option key={t.work_type_id} value={t.work_type_id}>{t.work_type_name}</option>)}
                  </select>
                </Field>
                <div className="md:col-span-2">
                  <Field label="Stage Name *">
                    <input className={inp} value={form.name || ''} onChange={(e) => setField('name', e.target.value)} placeholder="e.g. Estimate Stage" />
                  </Field>
                </div>
                <Field label="Code *">
                  <input className={inp} value={form.code || ''} onChange={(e) => setField('code', e.target.value.toUpperCase().replace(/\s+/g, '_'))} placeholder="e.g. SUB_ESTIMATE" />
                </Field>
                <Field label="Sequence Order">
                  <input type="number" className={inp} value={form.sequence_order ?? 0} onChange={(e) => setField('sequence_order', e.target.value)} />
                </Field>
              </div>
              <label className="flex items-center text-sm text-gray-700">
                <input type="checkbox" className="mr-2 h-4 w-4 rounded border-gray-300 text-emerald-600"
                  checked={form.is_active !== false} onChange={(e) => setField('is_active', e.target.checked)} />
                Active
              </label>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={handleSave} disabled={saving}
                className="inline-flex items-center px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-60">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {editingId ? 'Save Changes' : 'Create Stage'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <div className="flex items-center mb-3">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center mr-3"><Trash2 className="w-5 h-5 text-red-600" /></div>
              <h2 className="text-lg font-semibold text-gray-900">Delete Stage</h2>
            </div>
            <p className="text-sm text-gray-600 mb-5">
              Delete <span className="font-medium">{deleteTarget.name}</span> ({deleteTarget.code})? This removes it from the
              stages master and its dropdowns. Existing works keep their recorded stage text.
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDeleteTarget(null)} className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={handleDelete} disabled={deleting}
                className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-60">
                {deleting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const inp = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm';

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
    {children}
  </div>
);

export default StagesManagement;
